import { useState, useEffect } from "react";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "../Components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../Components/ui/table";

import DangerButton from "./DangerButton";
import { TbFilter } from "react-icons/tb";
import SecondaryButton from "./SecondaryButton";

export default function DataTable({
  initialData,
  columns,
  onDeleteSelected,
  pagination,
  title,
  headerItem,
  headerLastItem,
  header = true,
  className,
  rowOnClick = null,
}) {
  const [data, setData] = useState(initialData);
  const [sorting, setSorting] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [rowSelection, setRowSelection] = useState({});
  const [isDeleteDisabled, setIsDeleteDisabled] = useState(true);
  const [pageIndex, setPageIndex] = useState(0);
  const ITEMS_PER_PAGE = 6;

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      pagination: {
        pageIndex,
        pageSize: ITEMS_PER_PAGE,
      },
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    manualPagination: true,
    pageCount: Math.ceil(data.length / ITEMS_PER_PAGE),
  });

  useEffect(() => {
    setIsDeleteDisabled(table.getFilteredSelectedRowModel().rows.length === 0);
  }, [rowSelection, table]);

  // Handle external data changes
  useEffect(() => {
    setData(initialData);
  }, [initialData]);

  const handleDeleteSelected = async () => {
    const selectedIds = new Set(
      table.getSelectedRowModel().rows.map((row) => row.original.id)
    );
    const newData = data.filter((item) => !selectedIds.has(item.id));
    setData(newData);
    table.resetRowSelection(); // Reset row selection to avoid toggling select all checkbox

    await onDeleteSelected(Array.from(selectedIds));

    // toast.success("Selected rows deleted successfully.");
  };

  return (
    <div className="max-w-full w-full h-full">
      <div className="w-full h-full">
        {header ? (
          <div className="flex justify-between items-center py-4">
            <div className="flex gap-2 items-center">
              {headerItem ? headerItem : null}

              <DropdownMenu className="">
                <DropdownMenuTrigger className="border-zinc-800" asChild>
                  <SecondaryButton className="rounded-md text-white px-3.5">
                    <TbFilter className="w-5 h-5 mr-1.5 text-white" />
                    <span>View Filters</span>
                  </SecondaryButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-white" align="start">
                  {table
                    .getAllColumns()
                    .filter((column) => column.getCanHide())
                    .map((column) => {
                      return (
                        <DropdownMenuCheckboxItem
                          key={column.id}
                          className="capitalize"
                          checked={column.getIsVisible()}
                          onCheckedChange={(value) =>
                            column.toggleVisibility(!!value)
                          }
                        >
                          {column.id.replace("_", " ")}
                        </DropdownMenuCheckboxItem>
                      );
                    })}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {headerLastItem ? headerLastItem : null}
          </div>
        ) : null}
        <div
          className={[
            "shadow-lg shadow-black/[0.03] rounded-xl text-white border border-zinc-800 bg-transparent",
            className,
          ].join(" ")}
        >
          <Table className="!text-white">
            <TableHeader className="!text-white">
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow
                  className="border-zinc-800 !text-white "
                  key={headerGroup.id}
                >
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead
                        className="bg-transparent !text-white"
                        key={header.id}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody className="bg-transparent">
              {table.getRowModel().rows?.length ? (
                table
                  .getRowModel()
                  .rows.slice(
                    pageIndex * ITEMS_PER_PAGE,
                    (pageIndex + 1) * ITEMS_PER_PAGE
                  )
                  .map((row) => (
                    <TableRow
                      onClick={() => {
                        if (typeof rowOnClick === "function") {
                          rowOnClick({ row });
                        }
                      }}
                      key={row.id}
                      data-state={row.getIsSelected() && "selected"}
                      className={[
                        typeof rowOnClick === "function"
                          ? "cursor-pointer hover:bg-zinc-800/10 group"
                          : null,
                        "border-b border-zinc-800",
                      ].join(" ")}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell
                          key={cell.id}
                          className="group-last:first:rounded-bl-xl  group-last:last:rounded-br-xl"
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No results.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        {pagination && (
          <div className="flex items-center justify-end space-x-2 py-4">
            <div className="flex space-x-2">
              {onDeleteSelected && (
                <DangerButton
                  className="rounded-md !px-3 !py-1.5"
                  onClick={handleDeleteSelected}
                  disabled={isDeleteDisabled}
                >
                  Delete Selected
                </DangerButton>
              )}
              <SecondaryButton
                onClick={() => setPageIndex((prev) => Math.max(prev - 1, 0))}
                disabled={pageIndex === 0}
                className="bg-white text-zinc-700"
              >
                Previous
              </SecondaryButton>
              <SecondaryButton
                onClick={() => setPageIndex((prev) => prev + 1)}
                disabled={
                  pageIndex >= Math.ceil(data.length / ITEMS_PER_PAGE) - 1
                }
                className="bg-white text-zinc-700"
              >
                Next
              </SecondaryButton>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
